import apiClient from './apiClient';
import type { Patient } from './patientApi';

export interface DemoScriptText {
  en: string;
  ar: string;
  /** Code-switched Arabic+English, for sttLanguage 'auto'. */
  mixed?: string;
}

export interface DemoScripts {
  ambient: DemoScriptText;
  dictate: DemoScriptText;
}

export interface DemoPreview {
  source: 'cache' | 'fallback';
  specialty: string;
  patientPreview: {
    nameEn: string;
    nameAr: string;
    gender: string;
    age: number;
    summaryEn: string;
    summaryAr: string;
  };
  suggestedScript: DemoScriptText;
  /** Mode-specific variants; absent on older backends. */
  scripts?: DemoScripts;
  demoPatientExists: boolean;
}

export interface DemoPatientResult {
  patient: Patient;
  seededRecordId: string | null;
  // Script matching the profile the patient was ACTUALLY seeded from
  // (can differ from the preview's when the specialty profile finished
  // generating between preview and create). Optional: absent from
  // responses served by a not-yet-redeployed backend.
  suggestedScript?: DemoScriptText;
  scripts?: DemoScripts;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

const unwrap = <T>(payload: ApiEnvelope<T> | T): T => {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in (payload as object) &&
    'data' in (payload as object)
  ) {
    return (payload as ApiEnvelope<T>).data;
  }
  return payload as T;
};

/**
 * The suggested read-aloud script lives on /welcome, but recording
 * happens on /home — stash it here (keyed to the demo patient) so the
 * RecordingDock can keep it on screen while the doctor reads it.
 * sessionStorage: per-tab, gone when the tab closes; the next demo run
 * overwrites it.
 */
const DEMO_SCRIPT_STORAGE_KEY = 'maie.demoConsultScript';

export interface StashedDemoScript {
  patientId: string;
  en: string;
  ar: string;
}

export const stashDemoScript = (script: StashedDemoScript): void => {
  try {
    sessionStorage.setItem(DEMO_SCRIPT_STORAGE_KEY, JSON.stringify(script));
  } catch {
    /* storage unavailable — the demo still works without the card */
  }
};

export const readDemoScript = (): StashedDemoScript | null => {
  try {
    const raw = sessionStorage.getItem(DEMO_SCRIPT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StashedDemoScript>;
    if (!parsed.patientId || (!parsed.en && !parsed.ar)) return null;
    return {
      patientId: parsed.patientId,
      en: parsed.en ?? '',
      ar: parsed.ar ?? '',
    };
  } catch {
    return null;
  }
};

export const clearDemoScript = (): void => {
  try {
    sessionStorage.removeItem(DEMO_SCRIPT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * First-run demo consult endpoints. `preview` doubles as the platform
 * profile-cache warm trigger (call on /welcome mount + after specialty
 * change); `createPatient` is idempotent find-or-create.
 */
export const demoConsultApi = {
  preview: async (): Promise<DemoPreview> => {
    const { data } =
      await apiClient.get<ApiEnvelope<DemoPreview>>('/demo-consult/preview');
    return unwrap(data);
  },

  createPatient: async (): Promise<DemoPatientResult> => {
    const { data } = await apiClient.post<ApiEnvelope<DemoPatientResult>>(
      '/demo-consult/patient',
      {},
    );
    return unwrap(data);
  },

  deleteDemoData: async (): Promise<void> => {
    await apiClient.delete('/demo-consult/patient');
  },
};
